import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import AddIcon from '@atlaskit/icon/core/add';
import Image from '@atlaskit/image/image';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { usePinsAnalytics } from '../../analytics/usePinsAnalytics';
import PinEmptyStateImageDark from '../../assets/pin-empty-state-dark.svg';
import PinEmptyStateImage from '../../assets/pin-empty-state.svg';
import { usePinnedItems } from '../../services/pinned-items-store';
import type { PinnedItem } from '../../types';
import { PinnedItemRow } from '../pinned-item-row/PinnedItemRow';

import messages from './messages';
import { PinnedWorkSkeleton } from './PinnedWorkSkeleton';

const styles = cssMap({
	emptyState: {
		width: '100%',
		borderStyle: 'dashed',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.xxlarge'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	message: {
		flex: '1',
	},
});

/**
 * Intrinsic size of the empty-state illustration, matching the asset's cropped viewBox so it
 * renders 1:1 in the chat mini-modal — the default surface for this section. In wider containers
 * (fullscreen chat) it deliberately keeps this size and stays left-aligned rather than
 * stretching, per the design.
 */
const EMPTY_STATE_IMAGE_WIDTH = 340;
const EMPTY_STATE_IMAGE_HEIGHT = 80;

interface PinnedWorkSectionProps {
	cloudId: string;
	product?: string;
	/** Whether the containing Your Work view is currently visible. */
	isActive?: boolean;
	/** Called when the "+" button or, in the empty state, the "Pin work" button is clicked. */
	onPinWorkClick: () => void;
	/** Called when a pinned item's "Start chat" button is clicked. */
	onStartChat: (item: PinnedItem) => void;
}

/**
 * Pinned work section of the "For You" tab. When no items are pinned, renders only the
 * empty-state prompt. Once at least one item is pinned, renders a "Pinned work" heading
 * with a "+" button (navigates to search) followed by the list of pinned items instead.
 */
export const PinnedWorkSection = ({
	cloudId,
	product,
	isActive = true,
	onPinWorkClick,
	onStartChat,
}: PinnedWorkSectionProps): React.JSX.Element => {
	const intl = useIntl();
	const { items, isLoading, unpin } = usePinnedItems({ cloudId, product });
	const { firePinnedWorkSectionViewed } = usePinsAnalytics();
	const pinnedWorkState = items.length === 0 ? 'empty' : 'populated';
	const lastViewedStateRef = useRef<typeof pinnedWorkState | null>(null);

	useEffect(() => {
		if (!isActive) {
			lastViewedStateRef.current = null;
			return;
		}
		if (isLoading || lastViewedStateRef.current === pinnedWorkState) {
			return;
		}
		lastViewedStateRef.current = pinnedWorkState;
		firePinnedWorkSectionViewed({ state: pinnedWorkState, pinCount: items.length });
	}, [firePinnedWorkSectionViewed, isActive, isLoading, items.length, pinnedWorkState]);

	return (
		<Stack testId="rovo-for-you-pinned-work-section" space="space.150">
			{isLoading ? (
				<PinnedWorkSkeleton />
			) : items.length === 0 ? (
				<Box xcss={styles.emptyState} testId="rovo-for-you-pinned-work-empty-state">
					<Stack space="space.200">
						<Flex alignItems="center" justifyContent="space-between" gap="space.150">
							<Box xcss={styles.message}>
								<Text color="color.text.subtle">
									{intl.formatMessage(messages.pinnedWorkEmptyStateMessage)}
								</Text>
							</Box>
							<Button
								appearance="default"
								onClick={onPinWorkClick}
								testId="rovo-for-you-pinned-work-pin-button"
							>
								{intl.formatMessage(messages.pinnedWorkPinButtonLabel)}
							</Button>
						</Flex>
						{/* Decorative preview of what pinned rows look like — `alt=""` keeps it out of
						    the accessibility tree, since the message above already carries the meaning. */}
						<Image
							src={PinEmptyStateImage}
							srcDark={PinEmptyStateImageDark}
							alt=""
							width={EMPTY_STATE_IMAGE_WIDTH}
							height={EMPTY_STATE_IMAGE_HEIGHT}
							testId="rovo-for-you-pinned-work-empty-state-image"
						/>
					</Stack>
				</Box>
			) : (
				<>
					<Flex alignItems="center" justifyContent="space-between">
						<Heading size="xxsmall" as="h4">
							{intl.formatMessage(messages.pinnedWorkHeading)}
						</Heading>
						<IconButton
							icon={AddIcon}
							label={intl.formatMessage(messages.pinnedWorkAddButtonLabel)}
							appearance="subtle"
							onClick={onPinWorkClick}
							testId="rovo-for-you-pinned-work-add-button"
						/>
					</Flex>
					<Stack testId="rovo-for-you-pinned-work-list">
						{items.map((item) => (
							<PinnedItemRow
								key={item.id}
								type={item.pinType}
								title={item.name}
								onOpen={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
								onStartChat={() => onStartChat(item)}
								onUnpin={() => unpin(item.ari)}
							/>
						))}
					</Stack>
				</>
			)}
		</Stack>
	);
};
